import * as React from "react";
import { readableTimeSince } from './util';

import { useQuery } from '@apollo/react-hooks';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

import gql from "graphql-tag";

const LATEST_COMPANIES = gql`
    query {
        allCompanies(sortField: "id", sortOrder: "desc", page: 0, perPage: 6) {
            id
            name
            type
            company_trade
            location
            created_at
        }
    }`;

const LATEST_USERS = gql`
    query {
        allUsers(sortField: "id", sortOrder: "desc", page: 0, perPage: 6) {
            id
            first_name
            last_name
            email
            company {
                id
                name
            }
            created_at
        }
    }`;

const LATEST_DRAWINGS = gql`
    query {
        allDrawings(sortField: "id", sortOrder: "desc", page: 0, perPage: 6) {
            id
            display_name
            created_at
            project {
                name
            }
        }
    }`;

const COMPANY_METRICS = gql`
    query {
        totalProjectsVip: _allProjectsMeta(filter: {classification: "vip"}) {
            count
        }
        totalProjectsSelfService: _allProjectsMeta(filter: {classification: "self-service"}) {
            count
        }
        projectDataVip: allProjects(filter: {classification: "vip"}) {
            companies {
                name
                company_trade
            }
        }
        projectDataSelfService: allProjects(filter: {classification: "self-service"}) {
            companies {
                name
                company_trade
            }
        }
    }`;

function calculateMetrics(data) {
  const totalProjectsVip = data.totalProjectsVip.count;
  const totalProjectsSelfService = data.totalProjectsSelfService.count;
  const totalProjects = totalProjectsVip + totalProjectsSelfService;

  const totalCompaniesOnVipProjectsData = data.projectDataVip;
  const totalCompaniesOnSelfServiceProjectsData = data.projectDataSelfService;

  const totalCompaniesOnVipProjects = totalCompaniesOnVipProjectsData.reduce((a,c) => a+c.companies.length, 0);
  const totalCompaniesOnSelfServiceProjects = totalCompaniesOnSelfServiceProjectsData.reduce((a,c) => a+c.companies.length, 0);

  const averageCompaniesPerVipProject =  totalCompaniesOnVipProjects / totalProjectsVip;
  const averageCompaniesPerSelfServiceProject = totalCompaniesOnSelfServiceProjects / totalProjectsSelfService;
  const averageCompaniesPerProject = (totalCompaniesOnVipProjects + totalCompaniesOnSelfServiceProjects) / totalProjects;


  const allProjects = [...totalCompaniesOnVipProjectsData, ...totalCompaniesOnSelfServiceProjectsData];
  let allUniqueCompanies = {};
  let allUniqueTrades = {};
  let totalTradesPerProject = 0;
  let totalCompaniesPerTrade = 0;
  for (let p of allProjects) {
    let tradesOnProject = {};
    for (let c of p.companies) {
      allUniqueCompanies[c.name] = true;
      if (c.company_trade == 'Façade')  c.company_trade = 'Facade';
      allUniqueTrades[c.company_trade] = true;
      tradesOnProject[c.company_trade] = true;
    }
    let totalTrades = Math.max(Object.keys(tradesOnProject).length, 1);
    totalTradesPerProject += totalTrades;
    totalCompaniesPerTrade += p.companies.length / totalTrades;
  }
  const averageTradesPerProject = totalTradesPerProject / allProjects.length;
  const totalCompanies = Object.keys(allUniqueCompanies).length;
  const totalTrades = Object.keys(allUniqueTrades).length;

  const averageCompaniesPerTrade = totalCompaniesPerTrade / allProjects.length;

  return [
    {
      name: '# of Projects (vip + self-service)',
      value: totalProjectsVip + totalProjectsSelfService,
    },
    {
      name: '# of Projects (vip)',
      value: totalProjectsVip,
    },
    {
      name: '# of Projects (self-service)',
      value: totalProjectsSelfService,
    },
    {
      name: '# of Companies',
      value: totalCompanies,
    },
    {
      name: '# of Trades',
      value: totalTrades,
    },
    {
      name: 'Average Companies Per Project',
      value: averageCompaniesPerProject,
    },
    {
      name: 'Average Companies Per Project (vip)',
      value: averageCompaniesPerVipProject,
    },
    {
      name: 'Average Companies Per Project (self-service)',
      value: averageCompaniesPerSelfServiceProject,
    },
    {
      name: 'Average Trades Per Project',
      value: averageTradesPerProject,
    },
    {
      name: 'Average Companies Per Trades',
      value: averageCompaniesPerTrade,
    },
  ];


}

export default function Dashboard(props) {
  const {loading: companyLoading, error: companyError, data: companyData } = useQuery(LATEST_COMPANIES, { pollInterval: 30000, fetchPolicy: 'cache-and-network' });
  const {loading: usersLoading, error: usersError, data: usersData } = useQuery(LATEST_USERS, { pollInterval: 30000, fetchPolicy: 'cache-and-network' });
  const {loading: drawingLoading, error: drawingError, data: drawingData } = useQuery(LATEST_DRAWINGS, { pollInterval: 30000, fetchPolicy: 'cache-and-network' });
  const {loading: metricsLoading, error: metricsError, data: metricsData } = useQuery(COMPANY_METRICS, { pollInterval: 30000, fetchPolicy: 'cache-and-network' });

  if (companyLoading || drawingLoading || usersLoading || metricsLoading) { return <div/>; }
  if (companyError || usersError || drawingError || metricsError ) {
    console.log({
      companyError,
      usersError,
      drawingError,
      metricsError,
    });
    return <div/>;
  }
  const latestCompanies = companyData.allCompanies;
  const latestUsers = usersData.allUsers;
  const latestDrawings = drawingData.allDrawings;

  const metrics = calculateMetrics(metricsData);

  return (
    <Grid container spacing={3}>
      <Grid item xs={6}>
        <Card>
          <CardHeader title='Matrak Metrics' />
          <CardContent>
            <TableContainer component={Paper}>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell>Metric</TableCell>
                    <TableCell>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metrics.map((row, i) => (
                    <TableRow key={row.name+i}>
                      <TableCell component="th" scope="row">{row.name}</TableCell>
                      <TableCell>{row.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <br/>
            <Typography>The above refers to companies + trades on vip/self-service projects</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6}>
        <Card>
          <CardHeader title='Latest Users' />
          <CardContent>
            <TableContainer component={Paper}>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {latestUsers.map((row) => (
                    <TableRow key={row.name}>
                      <TableCell component="th" scope="row">
                        {`${row.first_name} ${row.last_name} (${row.email})`}
                      </TableCell>
                      <TableCell>
                        <Link href={`#/Company/${row.company.id}/show`}>
                          {row.company.name}
                        </Link>
                      </TableCell>
                      <TableCell>{readableTimeSince(new Date(row.created_at))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6}>
        <Card>
          <CardHeader title='Latest Companies' />
          <CardContent>
            <TableContainer component={Paper}>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {latestCompanies.map((row) => (
                    <TableRow key={row.name}>
                      <TableCell component="th" scope="row">
                        <Link href={`#/Company/${row.id}/show`}>
                          {row.name}
                        </Link>
                      </TableCell>
                      <TableCell>{readableTimeSince(new Date(row.created_at))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6}>
        <Card>
          <CardHeader title='Latest Drawings' />
          <CardContent>
            <TableContainer component={Paper}>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell>Display Name</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {latestDrawings.map((row) => (
                    <TableRow key={row.display_name}>
                      <TableCell component="th" scope="row">
                        {row.display_name}
                      </TableCell>
                      <TableCell>{row.project.name}</TableCell>
                      <TableCell>{readableTimeSince(new Date(row.created_at))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
};
